/**
 * Ícone do item de checklist.
 *
 * O catálogo não guarda ícone: ele é deduzido do código/rótulo do item por
 * palavra-chave. Item sem palavra conhecida ganha o ícone genérico — nunca
 * quebra, só fica menos expressivo.
 */
import type { ReactNode } from "react";

type Familia =
  | "pneu" | "freio" | "oleo" | "luz" | "vidro" | "cinto" | "documento" | "ferramenta"
  | "eletrico" | "suspensao" | "combustivel" | "limpeza" | "banco" | "porta" | "motor" | "painel" | "generico";

const REGRAS: Array<[Familia, RegExp]> = [
  ["pneu", /pneu|roda|estepe|calibr|rodoar|tracao|balancea|alinha/],
  ["freio", /freio|pastilha|tambor|lona|pedal/],
  ["oleo", /oleo|filtro|radiador|fluido|fluid|vazamento/],
  ["luz", /farol|farois|lanterna|seta|luz|lampada/],
  ["vidro", /retrovisor|espelho|para_brisa|brisa|vidro/],
  ["cinto", /cinto|seguranca|encosto_cabeca/],
  ["documento", /document|placa|tacografo|crlv|tara|licenc/],
  ["ferramenta", /triangulo|extintor|macaco|chave|cabo_aco|cabo_forca|ferrament|pinos|fixacao/],
  ["eletrico", /bateria|alternador|chicote|arranque|corrente|eletric|interclima/],
  ["suspensao", /suspens|amortecedor|mola/],
  ["combustivel", /combust|tanque|diesel|arla/],
  ["limpeza", /limpeza|limpo|higien/],
  ["banco", /banco|estofamento|cortina/],
  ["porta", /porta|trava|abertura/],
  ["motor", /motor|cambio|embreagem|direcao|turbo/],
  ["painel", /painel|instrumento/],
];

/** Sem acento e em minúsculas: casa "Faróis" e "farois" do mesmo jeito. */
function normaliza(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, "_");
}

export function familiaDoItem(code: string, label: string): Familia {
  const alvo = `${normaliza(code)} ${normaliza(label)}`;
  return REGRAS.find(([, re]) => re.test(alvo))?.[0] ?? "generico";
}

const DESENHO: Record<Familia, ReactNode> = {
  pneu: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.5" /><path d="M12 3v5.5M12 15.5V21M3 12h5.5M15.5 12H21" /></>,
  freio: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="2.5" /><circle cx="12" cy="6.5" r="1" /><circle cx="17" cy="14.5" r="1" /><circle cx="7" cy="14.5" r="1" /></>,
  oleo: <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z" />,
  luz: <><path d="M9 21h6M10 17h4" /><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3Z" /></>,
  vidro: <><rect x="3" y="5" width="18" height="12" rx="2" /><path d="M7 21h10M12 17v4" /></>,
  cinto: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />,
  documento: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" /><path d="M14 2v6h6M16 13H8M16 17H8" /></>,
  ferramenta: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z" />,
  eletrico: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />,
  suspensao: <path d="M8 3h8M8 21h8M9 5l6 2.5L9 10l6 2.5L9 15l6 2.5L9 20" />,
  combustivel: <><path d="M3 22h12M4 9h10M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18" /><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 4 0V9.8a2 2 0 0 0-.6-1.4L18 5" /></>,
  limpeza: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3ZM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16Z" />,
  banco: <path d="M6 3h6a3 3 0 0 1 3 3v8H8a2 2 0 0 1-2-2V3ZM4 14h14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4ZM8 20v2M16 20v2" />,
  porta: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M15 12h.01" /></>,
  motor: <path d="M3 10v6h3l2 2h8l2-2h3v-6h-2l-1-2h-3V6H9v2H6l-1 2H3Z" />,
  painel: <><circle cx="12" cy="13" r="8" /><path d="M12 13l4-4M12 5v2M4.9 7.9l1.4 1.4M19.1 7.9l-1.4 1.4" /></>,
  generico: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="m9 12 2.2 2.2L15.5 10" /></>,
};

export function ItemIcon({ code, label, className = "w-5 h-5" }: { code: string; label: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {DESENHO[familiaDoItem(code, label)]}
    </svg>
  );
}
